"use client";

import { validateLeaveRequest } from '@/lib/hrApi';
import { toDisplayDate } from '@/lib/orgFormat';
import React, { useMemo, useState, useEffect } from "react";
import { getApiErrorMessage } from '@/lib/apiError';
import { Calendar, MapPin, Plus, Clock, Plane, Users, X, ChevronLeft } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { useTranslation } from "react-i18next";

import { RequestTimeOffForm } from "../../time-off/request-time-off-form";
import { MyUnaccountedDays } from "./MyUnaccountedDays";
import { TimeOffHistory } from "../../time-off/time-off-history";
import { PendingRequestsCard } from "../../time-off/pending-requests-card";
import { TeamAbsences } from "../../time-off/team-absences";
import { ApprovalsPanel } from "../../time-off/approvals-panel";
import { useC, useDark, type C as ColorSet } from "@/features/shared/theme/kaziColors";
import { useIsMobile } from "@/features/shared/hooks/use-mobile";
import { getMyProfile, getCachedProfile, getLeaveBalances, createLeaveRequest, getMyLeaveRequests, getPendingApprovals, getLeaveEvidenceRules, uploadDocument, getCoverageRequirement, type CoverageRequirement } from "@/lib/hrApi";
import { useRoleAccess } from "@/features/hooks/useRoleAccess";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { appRoute, saveLeaveDraft, takeLeaveDraft, type LeavePrefill } from "@/features/delegation/leaveBridge";
import type { CoverageDraft } from "../../time-off/request-time-off-form";
import { deriveBalance, bookingLedgerRows } from "./balance";
import { getMyLeaveEmployments, type LeaveEmployment } from "@/lib/hrApi";

type ViewState = "dashboard" | "request-form" | "history";
type TabKey = "vacation" | "sick" | "workFromHome";

const EMPTY_USER = { name: "", position: "", joinDate: "", location: "", manager: "", avatarUrl: "" };

// Map the employee profile (from /me/profile, which embeds manager + addresses).
const fullName = (e?: any) => (e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() : '');
const primaryAddress = (p?: any) => {
  const addrs = Array.isArray(p?.addresses) ? p.addresses : [];
  return addrs.find((a: any) => a?.is_primary) || addrs[0];
};
const toLocation = (p?: any) => {
  const a = primaryAddress(p);
  const parts = [a?.city, a?.region].filter(Boolean);
  const fromAddr = [...new Set(parts)].join(', ');
  return fromAddr || p?.nationality || a?.country || '';
};
const toSidebarUser = (p?: any) => ({
  name: fullName(p) || p?.email || '',
  position: p?.position || '',
  joinDate: (p?.hire_date || '').slice(0, 10),
  location: toLocation(p),
  manager: fullName(p?.manager),
  avatarUrl: p?.profile_picture_url || '',
});

const AV_COLORS = ["#F28A3A", "#3B82F6", "#22C55E", "#8B5CF6", "#EF4444", "#F6B26B"];
function Avatar({ name, size = 34, url }: { name: string; size?: number; url?: string }) {
  const initials = (name || '?').split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const ci = (name || '?').charCodeAt(0) % AV_COLORS.length;
  if (url) {
    return <img src={url} alt={name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: AV_COLORS[ci], display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function TabButton({ icon: Icon, label, active, onClick, C }: {
  icon: React.ComponentType<any>; label: string; active?: boolean; onClick?: () => void; C: ColorSet;
}) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 10,
      border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit",
      transition: "all 0.2s", borderBottom: `3px solid ${active ? C.orange : "transparent"}`,
    }}>
      <Icon size={16} color={active ? C.orange : C.charcoalLight} />
      <span style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? C.orange : C.charcoalMid }}>{label}</span>
    </button>
  );
}

function MetricCard({ icon: Icon, label, value, subtitle, C, dark }: {
  icon: React.ComponentType<any>; label: string; value: string; subtitle: string; C: ColorSet; dark: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px", background: dark ? C.creamDeep : C.white, borderRadius: 14, border: `1px solid ${C.border}` }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={22} color={C.orange} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: C.charcoalLight, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 800, color: C.charcoal, letterSpacing: "-0.02em", lineHeight: 1, marginBottom: 4 }}>{value}</p>
        {subtitle ? <p style={{ fontSize: 11, color: C.charcoalLight }}>{subtitle}</p> : null}
      </div>
    </div>
  );
}

// Modal shell (replaces MUI Dialog) — plain, theme-aware.
function Modal({ open, onClose, children, C }: { open: boolean; onClose: () => void; children: React.ReactNode; C: ColorSet }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1300, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.white, borderRadius: 18, border: `1px solid ${C.border}`, width: "100%", maxWidth: 760, maxHeight: "90vh", overflow: "auto", position: "relative" }}>
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 14, right: 14, zIndex: 2, width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", color: C.charcoalLight, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

export function Page() {
  const { t } = useTranslation("timeoff");
  const C = useC();
  const dark = useDark();
  const isMobile = useIsMobile();
  const [viewState, setViewState] = useState<ViewState>("dashboard");
  const location = useLocation();
  const navigate = useNavigate();
  // Delegation ↔ Leave: prefill from a delegation ("Continue to Leave Request"),
  // or restore the draft we kept while the employee set up a delegation.
  const [prefill, setPrefill] = useState<(LeavePrefill & { type?: string; notes?: string; dayType?: string }) | undefined>(undefined);
  const [coverage, setCoverage] = useState<CoverageDraft | null>(null);
  const [coverageRequirement, setCoverageRequirement] = useState<CoverageRequirement | null>(null);
  useEffect(() => { getCoverageRequirement().then(setCoverageRequirement).catch(() => setCoverageRequirement(null)); }, []);
  useEffect(() => {
    const st = (location.state ?? {}) as { prefill?: LeavePrefill; openRequestId?: string; delegationCancelled?: boolean; delegationCreated?: { id: string; delegateName?: string | null; status?: string; ccCount?: number; startsAt?: string; endsAt?: string } };
    if (st.openRequestId) {
      setViewState("history");
      window.history.replaceState({}, '');
      return;
    }
    if (st.prefill) {
      setPrefill({ startDate: st.prefill.startDate, endDate: st.prefill.endDate, reason: st.prefill.reason });
      if (st.prefill.delegationId) setCoverage({ delegationId: st.prefill.delegationId, delegateName: st.prefill.delegateName ?? null, status: 'active' });
      setViewState("request-form");
      toast.success(t('timeoff:delegation_created_prefill', { name: st.prefill.delegateName || '', defaultValue: 'Dates and coverage prefilled from your delegation to {{name}}.' }));
      window.history.replaceState({}, '');
      return;
    }
    // Came back from delegation setup without creating one. Restore exactly
    // what they had typed and say nothing: abandoning coverage setup is not an
    // event worth a toast, but losing the draft would be.
    if (st.delegationCancelled) {
      const draft = takeLeaveDraft();
      if (draft) {
        setPrefill({ type: draft.type, startDate: draft.startDate, endDate: draft.endDate, reason: draft.reason, notes: draft.notes, dayType: draft.dayType });
      }
      setViewState("request-form");
      window.history.replaceState({}, '');
      return;
    }
    if (st.delegationCreated) {
      const draft = takeLeaveDraft();
      setPrefill(draft ? { type: draft.type, startDate: draft.startDate, endDate: draft.endDate, reason: draft.reason, notes: draft.notes, dayType: draft.dayType } : undefined);
      setCoverage({ delegationId: st.delegationCreated.id, delegateName: st.delegationCreated.delegateName ?? null, status: st.delegationCreated.status ?? 'active', ccCount: st.delegationCreated.ccCount });
      setViewState("request-form");
      if (draft) toast.success(t('timeoff:draft_restored', { defaultValue: 'We kept your leave request draft.' }));
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const openDelegationFlow = (draft: { type: string; startDate: string; endDate: string; reason: string; notes: string; dayType: string }) => {
    saveLeaveDraft(draft);
    navigate(appRoute(location.pathname, 'admin/delegations'), {
      state: { fromLeave: { startDate: draft.startDate, endDate: draft.endDate, reason: draft.reason, returnTo: 'time-off' } },
    });
  };
  const [teamAbsencesOpen, setTeamAbsencesOpen] = useState(false);
  const [historyType, setHistoryType] = useState<'vacation' | 'sick' | 'wfh' | 'all'>('vacation');
  // Every request (every leave type) — the pending ones are listed by name.
  const [myRequests, setMyRequests] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>("vacation");

  const { isManager, isTenantAdmin } = useRoleAccess();
  const canApprove = isManager || isTenantAdmin;
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [user, setUser] = useState(EMPTY_USER);
  const [balances, setBalances] = useState({ vacation: 0, sick: 0, workFromHome: 0 });
  const [taken, setTaken] = useState({ vacation: 0, sick: 0, workFromHome: 0 });
  // Submitted-but-not-yet-approved days. They are already spoken for, so they
  // have to come off what the employee is told is left — otherwise someone
  // reads "21 Days Remaining", books all 21, and is refused because 5 were
  // pending all along.
  const [pending, setPending] = useState({ vacation: 0, sick: 0, workFromHome: 0 });
  // The server's own figure: entitled + carried_forward + adjusted - taken -
  // pending. Recomputing it here as (entitled - taken) silently dropped
  // carry-forward and manual adjustments as well as pending.
  const [available, setAvailable] = useState({ vacation: 0, sick: 0, workFromHome: 0 });
  const [percentages, setPercentages] = useState({ vacation: 0, sick: 0, workFromHome: 0 });
  const [nextLeave, setNextLeave] = useState<string | undefined>(undefined);
  const [takenByMonth, setTakenByMonth] = useState({
    vacation: Array(12).fill(0), sick: Array(12).fill(0), workFromHome: Array(12).fill(0),
  });
  const [availability, setAvailability] = useState<{ label: string; tone: 'available' | 'leave' | 'wfh' | 'inactive' } | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  // Multi-entity G5: someone employed by two companies sees one employment's
  // balances at a time (each employment has its own ledger).
  const [leaveEmployments, setLeaveEmployments] = useState<LeaveEmployment[]>([]);
  const [balanceEngagement, setBalanceEngagement] = useState<string>('');
  useEffect(() => {
    getMyLeaveEmployments()
      .then((r) => {
        if (!r.per_employment) return;
        setLeaveEmployments(r.employments);
        setBalanceEngagement((r.employments.find((e) => e.is_primary) ?? r.employments[0])?.engagement_id ?? '');
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const cached = getCachedProfile();
    if (cached) setUser(toSidebarUser(cached));
    let myStatus = (cached?.status as string) || 'Active';
    (async () => {
      try {
        const me = await getMyProfile();
        myStatus = me.status || 'Active';
        setUser(toSidebarUser(me));
      } catch { /* profile unavailable */ }

      try {
        const list = await getLeaveBalances();
        const key = (typ: string) => (typ === 'wfh' ? 'workFromHome' : typ) as TabKey;
        const b = { vacation: 0, sick: 0, workFromHome: 0 };
        const tk = { vacation: 0, sick: 0, workFromHome: 0 };
        const pc = { vacation: 0, sick: 0, workFromHome: 0 };
        const pd = { vacation: 0, sick: 0, workFromHome: 0 };
        const av = { vacation: 0, sick: 0, workFromHome: 0 };
        // The ledger the person books from — the same rule the dashboard's
        // headline uses (bookingLedgerRows), so the two can't disagree.
        bookingLedgerRows(Array.isArray(list) ? list : [],
          { per_employment: leaveEmployments.length > 0, employments: leaveEmployments }, balanceEngagement).forEach((row: any) => {
          const k = key(row.type);
          if (!(k in b)) return;
          const view = deriveBalance(row);
          b[k] = view.entitled;
          tk[k] = view.taken;
          pd[k] = view.pending;
          av[k] = view.remaining;
          pc[k] = view.percent;
        });
        setBalances(b); setTaken(tk); setPercentages(pc); setPending(pd); setAvailable(av);
      } catch { /* leave service unavailable */ }

      try {
        const raw = await getMyLeaveRequests();
        const reqs = Array.isArray(raw) ? (raw as any[]) : [];
        setMyRequests(reqs);
        // LOCAL date — the UTC date is yesterday for part of every day west of UTC.
        const _d = new Date();
        const _p = (n: number) => String(n).padStart(2, '0');
        const todayIso = `${_d.getFullYear()}-${_p(_d.getMonth() + 1)}-${_p(_d.getDate())}`;
        const tbm = { vacation: Array(12).fill(0), sick: Array(12).fill(0), workFromHome: Array(12).fill(0) };
        reqs.forEach((r: any) => {
          if (String(r.status || '').toLowerCase() !== 'approved') return;
          const code = String(r.leave_type?.type_code || r.type || '').toLowerCase();
          const k = code.includes('sick') ? 'sick' : (code.includes('wfh') || code.includes('home')) ? 'workFromHome' : 'vacation';
          // Civil date: new Date() would bucket a 1 May request into April
          // for any viewer west of UTC, quietly skewing the yearly chart.
          const m = toDisplayDate(r.start_date).getMonth();
          if (m >= 0 && m < 12) tbm[k as keyof typeof tbm][m] += Number(r.days_requested || r.days || 0);
        });
        setTakenByMonth(tbm);

        const upcoming = reqs.filter((r: any) => (r.start_date || '') >= todayIso).sort((a: any, b: any) => (a.start_date > b.start_date ? 1 : -1));
        if (upcoming.length) {
          const r = upcoming[0];
          // A leave date is a CIVIL date, not an instant. `new Date("2026-10-02")`
          // parses as UTC MIDNIGHT, so any viewer west of UTC renders the day before —
          // which is how an approver saw "Oct 1" for an Oct 2 request while the employee's
          // own history showed Oct 2. toDisplayDate anchors date-only strings to LOCAL
          // midnight so every surface agrees.
          const fmt = (d?: string) => (d ? toDisplayDate(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
          const end = r.end_date && r.end_date !== r.start_date ? ` – ${fmt(r.end_date)}` : '';
          setNextLeave(`${fmt(r.start_date)}${end}`);
        } else setNextLeave(undefined);

        const s = myStatus.toLowerCase();
        if (s === 'inactive' || s === 'terminated') {
          setAvailability({ label: myStatus, tone: 'inactive' });
        } else {
          const outToday = reqs.find((r: any) =>
            String(r.status || '').toLowerCase() === 'approved' &&
            (r.start_date || '') <= todayIso && (r.end_date || r.start_date || '') >= todayIso);
          if (outToday) {
            const code = String(outToday.type || outToday.leave_type?.type_code || '').toLowerCase();
            if (code.includes('sick')) setAvailability({ label: t('avail_out_sick', { defaultValue: 'Out Sick' }), tone: 'leave' });
            else if (code.includes('wfh') || code.includes('home')) setAvailability({ label: t('avail_wfh', { defaultValue: 'Working from Home' }), tone: 'wfh' });
            else if (code.includes('annual') || code.includes('vacation')) setAvailability({ label: t('avail_on_vacation', { defaultValue: 'On Vacation' }), tone: 'leave' });
            else setAvailability({ label: t('avail_on_leave', { defaultValue: 'On Leave' }), tone: 'leave' });
          } else setAvailability(undefined);
        }
      } catch { /* leave service unavailable */ }

      if (canApprove) {
        try {
          const p = await getPendingApprovals();
          setPendingCount(Array.isArray(p) ? p.length : 0);
        } catch { /* not a manager / unavailable */ }
      }
    })();
  }, [refreshKey, canApprove, balanceEngagement, leaveEmployments]);

  const [evidenceRules, setEvidenceRules] = useState<Record<string, number>>({});
  useEffect(() => { getLeaveEvidenceRules().then(setEvidenceRules).catch(() => setEvidenceRules({})); }, []);
  const [pendingEvidence, setPendingEvidence] = useState<{ data: { startDate: string; endDate: string; type: 'vacation' | 'sick' | 'wfh'; reason?: string }; threshold: number } | null>(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);

  const submitWithAttachments = async (data: { startDate: string; endDate: string; type: 'vacation' | 'sick' | 'wfh'; reason?: string; delegationId?: string | null; coverageAttestation?: { statement: string; signedName: string } | null }, attachments?: { document_id: string; file_name?: string }[]) => {
    await createLeaveRequest({ type: data.type, startDate: data.startDate, endDate: data.endDate || data.startDate, reason: data.reason || 'Time off request', attachments, dayType: (data as any).dayType, engagementId: (data as any).engagementId || undefined, engagementIds: (data as any).engagementIds || undefined, delegationId: data.delegationId ?? coverage?.delegationId ?? null, coverageAttestation: data.coverageAttestation ?? null });
    setCoverage(null);
    setPrefill(undefined);
    toast.success(t('timeoff:request_submitted', { defaultValue: 'Leave request submitted — pending approval' }));
    setViewState("dashboard");
    setRefreshKey((k) => k + 1);
  };

  const handleEvidenceFile = async (file: File) => {
    if (!pendingEvidence) return;
    // The upload needs the employee RECORD id — from the cached /me/profile
    // (the sidebar `user` state is display-shaped and has no id). The previous
    // `profile?.id` referenced an undefined identifier and threw a
    // ReferenceError the moment a file was chosen.
    const prof = getCachedProfile() ?? (await getMyProfile().catch(() => null));
    if (!prof?.id) {
      toast.error(t('timeoff:profile_unavailable', { defaultValue: 'Could not load your profile — try again' }));
      return;
    }
    setEvidenceUploading(true);
    try {
      const doc: any = await uploadDocument(String(prof.id), file, 'certificate');
      await submitWithAttachments(pendingEvidence.data, [{ document_id: String(doc.id), file_name: file.name }]);
      setPendingEvidence(null);
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Could not attach the document'));
    } finally {
      setEvidenceUploading(false);
    }
  };

  // Returns the refusal reason when the request was not accepted (the form
  // shows it next to Submit as well as in this toast).
  const handleRequestSubmit = async (data: { startDate: string; endDate: string; type: 'vacation' | 'sick' | 'wfh'; reason?: string }): Promise<string | void> => {
    try {
      // Evidence rule (R5): e.g. sick leave beyond N days needs a certificate.
      // The server keys rules by the lowercase frontend key ("vacation"/"sick"/
      // "wfh"); the form supplies the UPPERCASE type_code — normalize before
      // lookup or the prompt never fires and certificate-gated requests are
      // submitted without evidence.
      const ruleKey =
        ({ ANNUAL: 'vacation', SICK: 'sick', WFH: 'wfh' } as Record<string, string>)[data.type]
        ?? String(data.type).toLowerCase();
      const threshold = evidenceRules[ruleKey] ?? evidenceRules[data.type];
      if (threshold !== undefined) {
        // Phase 0 (F6): the gate compares WORKING days (server canonical
        // engine) against the working-day threshold — the old client
        // calendar-day count demanded certificates the server wouldn't.
        let days = 0;
        try {
          const v = await validateLeaveRequest({
            type: data.type, startDate: data.startDate,
            endDate: data.endDate || undefined, dayType: data.dayType || 'full',
          });
          days = v.days_requested;
        } catch {
          // validate unreachable → fail toward asking for evidence (safe side)
          const start = new Date(data.startDate);
          const end = new Date(data.endDate || data.startDate);
          days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        }
        if (days > threshold) {
          setPendingEvidence({ data, threshold });
          return;
        }
      }
      await submitWithAttachments(data);
    } catch (err: any) {
      // Surface the real reason (e.g. "Insufficient leave balance") instead of
      // failing silently, and keep the user on the form to correct + retry.
      const msg = getApiErrorMessage(err, t('timeoff:request_failed', { defaultValue: 'Failed to submit leave request' }));
      toast.error(msg);
      return msg;
    }
  };

  // ── Derived display values for the active tab ──────────────────────────────
  const tabMeta: Record<TabKey, { label: string; icon: any }> = {
    vacation: { label: t('annual', { defaultValue: 'Annual' }), icon: Plane },
    sick: { label: t('sick', { defaultValue: 'Sick Leave' }), icon: Plus },
    workFromHome: { label: t('wfh', { defaultValue: 'Work From Home' }), icon: Clock },
  };
  const entitled = balances[activeTab];
  const usedDays = taken[activeTab];
  const pendingDays = pending[activeTab];
  // The server's available, not a local subtraction — it is the same number the
  // Balance % is derived from, so the two cards can no longer contradict each
  // other (52 Days Remaining beside 98% Balance).
  const remaining = Math.max(0, available[activeTab]);
  const balancePct = percentages[activeTab];
  const accrualPerMonth = entitled > 0 ? (entitled / 12) : 0;

  const chartData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const tbm = takenByMonth[activeTab];
    return months.map((month, i) => ({
      month,
      accrued: Math.round(entitled * ((i + 1) / 12)),
      taken: tbm[i] || 0,
    }));
  }, [takenByMonth, activeTab, entitled]);

  const availChip = (() => {
    if (!availability) return { label: t('status_available', { defaultValue: 'Available' }), bg: C.greenLight, text: C.greenText, dot: C.green };
    if (availability.tone === 'leave') return { label: availability.label, bg: C.orangeLight, text: C.orangeDark, dot: C.orange };
    if (availability.tone === 'wfh') return { label: availability.label, bg: C.blueLight, text: C.blueText, dot: C.blue };
    if (availability.tone === 'inactive') return { label: availability.label, bg: C.creamDark, text: C.charcoalMid, dot: C.charcoalSubtle };
    return { label: availability.label, bg: C.greenLight, text: C.greenText, dot: C.green };
  })();

  const cardStyle: React.CSSProperties = { background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, transition: "background 0.25s" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "300px 1fr", gap: 24 }}>
      {/* ══ LEFT SIDEBAR ══ */}
      <aside style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Profile Card */}
        <div style={{ ...cardStyle, padding: "24px 20px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 }}>
            <Avatar name={user.name || '?'} size={72} url={user.avatarUrl || undefined} />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, marginTop: 12, marginBottom: 4 }}>{user.name || '—'}</h3>
            {user.position ? <p style={{ fontSize: 12, color: C.charcoalLight, marginBottom: 10 }}>{user.position}</p> : null}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 99, background: availChip.bg, fontSize: 11, fontWeight: 600, color: availChip.text }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: availChip.dot }} />
              {availChip.label}
            </span>
          </div>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {user.joinDate ? (
              <InfoRow C={C} icon={Calendar} label={t('joined', { defaultValue: 'Joined' })} value={user.joinDate} />
            ) : null}
            {user.location ? (
              <InfoRow C={C} icon={MapPin} label={t('location', { defaultValue: 'Location' })} value={user.location} />
            ) : null}
            {user.manager ? (
              <InfoRow C={C} icon={Users} label={t('manager', { defaultValue: 'Manager' })} value={user.manager} />
            ) : null}
          </div>
        </div>

        {/* Time-Off Status */}
        <div style={{ ...cardStyle, padding: 20 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 16 }}>{t('time_off_status', { defaultValue: 'Time-Off Status' })}</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <StatusRow C={C} dot={C.green} label={t('vacation', { defaultValue: 'Vacation' })} value={`${taken.vacation}/${balances.vacation}`} />
            <StatusRow C={C} dot={C.charcoalSubtle} label={t('sick_days', { defaultValue: 'Sick Days' })} value={`${taken.sick}/${balances.sick}`} />
          </div>
          {nextLeave ? (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: C.charcoalLight, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{t('next_leave', { defaultValue: 'Next Leave' })}</p>
              <p style={{ fontSize: 13, fontWeight: 600, color: C.charcoal }}>{nextLeave}</p>
            </div>
          ) : null}
        </div>

        {/* Quick Actions */}
        <div style={{ ...cardStyle, padding: 20 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 16 }}>{t('quick_actions', { defaultValue: 'Quick Actions' })}</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => setViewState("request-form")} style={primaryBtn(C)}>
              <Plus size={16} /> {t('request_leave', { defaultValue: 'Request Leave' })}
            </button>
            <button onClick={() => setTeamAbsencesOpen(true)} style={outlineBtn(C)}>
              <Plane size={16} /> {t('out_of_office', { defaultValue: 'Out of Office' })}
            </button>
            {canApprove ? (
              <button onClick={() => setApprovalsOpen(true)} style={outlineBtn(C)}>
                <Users size={16} /> {t('approvals', { defaultValue: 'Approvals' })}{pendingCount ? ` (${pendingCount})` : ''}
              </button>
            ) : null}
          </div>
        </div>
      </aside>

      {/* ══ MAIN CONTENT ══ */}
      <main style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.charcoal, letterSpacing: "-0.03em", marginBottom: 6 }}>
            {viewState === "request-form" ? t('title_request', { defaultValue: 'Request Time Off' }) :
             viewState === "history" ? t('title_history', { defaultValue: 'Leave History' }) :
             t('title_timeoff', { defaultValue: 'Time Off' })}
          </h1>
          <p style={{ fontSize: 14, color: C.charcoalLight }}>{t('timeoff_subtitle', { defaultValue: 'Manage your time off requests and balances' })}</p>
        </div>

        {viewState === "request-form" ? (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <RequestTimeOffForm
              onSubmit={handleRequestSubmit}
              onCancel={() => { setPrefill(undefined); setCoverage(null); setViewState("dashboard"); }}
              prefill={prefill}
              coverageRequirement={coverageRequirement}
              coverage={coverage}
              onSetUpDelegation={openDelegationFlow}
            />
            {pendingEvidence && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPendingEvidence(null)}>
                <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-neutral-900" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                    {t('timeoff:evidence_needed', { defaultValue: 'Supporting document needed' })}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-500">
                    {t('timeoff:evidence_note', { defaultValue: `Requests of this type beyond ${pendingEvidence.threshold} day(s) need a supporting document (e.g. a medical certificate). Attach it to submit your request.`, threshold: pendingEvidence.threshold })}
                  </p>
                  <label className="mt-4 block cursor-pointer rounded-xl border-2 border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500 hover:border-orange-400 dark:border-neutral-700">
                    {evidenceUploading
                      ? t('timeoff:uploading', { defaultValue: 'Uploading…' })
                      : t('timeoff:choose_file', { defaultValue: 'Choose a file (PDF or photo)' })}
                    <input type="file" accept="application/pdf,image/*" className="hidden" disabled={evidenceUploading}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void handleEvidenceFile(f); }} />
                  </label>
                  <div className="mt-4 flex justify-end">
                    <button type="button" onClick={() => setPendingEvidence(null)}
                      className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 dark:border-neutral-700 dark:text-neutral-300">
                      {t('cancel', { defaultValue: 'Cancel' })}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : viewState === "history" ? (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <button onClick={() => setViewState("dashboard")} style={{ ...outlineBtn(C), margin: 16, width: 'auto' }}>
              <ChevronLeft size={16} /> {t('back', { defaultValue: 'Back' })}
            </button>
            <TimeOffHistory type={historyType} onBack={() => setViewState("dashboard")} onChanged={() => setRefreshKey((k) => k + 1)} />
          </div>
        ) : (
          <div style={{ ...cardStyle, borderRadius: 18, overflow: "hidden" }}>
            {leaveEmployments.length > 1 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 24px 0' }}>
                <label htmlFor="balance-employment" style={{ fontSize: 12, fontWeight: 600, color: C.charcoalMid }}>
                  {t('balance_employment', { defaultValue: 'Balances for' })}
                </label>
                <select id="balance-employment" data-testid="balance-employment" value={balanceEngagement}
                  onChange={(e) => setBalanceEngagement(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: 13 }}>
                  {leaveEmployments.map((e) => <option key={e.engagement_id} value={e.engagement_id}>{e.company}</option>)}
                </select>
              </div>
            ) : null}
            {/* Tabs */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "16px 24px 0", borderBottom: `1px solid ${C.border}` }}>
              {(["vacation", "sick", "workFromHome"] as TabKey[]).map((k) => (
                <TabButton key={k} C={C} icon={tabMeta[k].icon} label={tabMeta[k].label} active={activeTab === k} onClick={() => setActiveTab(k)} />
              ))}
            </div>

            <div style={{ padding: 28 }}>
              {/* Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
                <MetricCard C={C} dark={dark} icon={Calendar} label={t('days_remaining', { defaultValue: 'Days Remaining' })} value={`${remaining} ${t('days', { defaultValue: 'Days' })}`} subtitle={pendingDays > 0
                  ? t('remaining_after_pending', { days: pendingDays, defaultValue: '{{days}} day(s) awaiting approval already deducted' })
                  : t('remaining', { defaultValue: 'Remaining' })} />
                <MetricCard C={C} dark={dark} icon={Clock} label={t('days_taken', { defaultValue: 'Days Taken' })} value={`${usedDays} ${t('days', { defaultValue: 'Days' })}`} subtitle={accrualPerMonth > 0 ? t('accruing_at', { days: accrualPerMonth.toFixed(2), defaultValue: 'Accruing at {{days}} days per month' }) : ''} />
                <MetricCard C={C} dark={dark} icon={Calendar} label={t('balance', { defaultValue: 'Balance' })} value={`${balancePct}%`} subtitle="" />
              </div>

              {/* Your own unaccounted days. Here rather than on a manager's
                  board because the person who can close a gap in one click is
                  the one who left it. */}
              <div style={{ marginBottom: 28 }}>
                <MyUnaccountedDays
                  onRequestLeave={(date) => {
                    setPrefill({ startDate: date, endDate: date });
                    setViewState("request-form");
                  }}
                />
              </div>

              {/* Pending requests of every type (Compassionate has no tab) */}
              <PendingRequestsCard requests={myRequests} onOpen={() => { setHistoryType('all'); setViewState("history"); }} />

              {/* Actions */}
              <div style={{ display: "flex", gap: 12, marginBottom: 28 }}>
                <button onClick={() => setViewState("request-form")} style={{ ...primaryBtn(C), width: 'auto', padding: "12px 24px" }}>{t('request', { defaultValue: 'Request' })}</button>
                <button onClick={() => { const ht = activeTab === 'workFromHome' ? 'wfh' : activeTab; setHistoryType(ht as any); setViewState("history"); }} style={{ ...outlineBtn(C), width: 'auto', padding: "12px 24px" }}>{t('history', { defaultValue: 'History' })}</button>
              </div>

              {/* Chart */}
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal, marginBottom: 20 }}>{t('annual_leave_summary', { defaultValue: 'Annual Leave Summary' })}</h3>
                <div style={{ background: dark ? C.creamDeep : C.cream, borderRadius: 14, padding: "24px 16px 16px", transition: 'background 0.25s' }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData} barGap={0}>
                      <CartesianGrid strokeDasharray="3 3" stroke={dark ? "rgba(255,255,255,0.05)" : "rgba(47,52,67,0.08)"} vertical={false} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: C.charcoalLight, fontSize: 11, fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: C.charcoalLight, fontSize: 11, fontWeight: 500 }} />
                      <RechartsTooltip contentStyle={{ background: dark ? C.creamDeep : "#fff", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: C.charcoal }} cursor={{ fill: dark ? "rgba(255,255,255,0.03)" : "rgba(47,52,67,0.03)" }} />
                      <Bar dataKey="accrued" fill={C.orange} radius={[6, 6, 0, 0]} name={t('accrued', { defaultValue: 'Accrued' })} />
                      <Bar dataKey="taken" fill={C.peach} radius={[6, 6, 0, 0]} name={t('taken', { defaultValue: 'Taken' })} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 16 }}>
                    <LegendDot C={C} color={C.orange} label={t('accrued', { defaultValue: 'Accrued' })} />
                    <LegendDot C={C} color={C.peach} label={t('taken', { defaultValue: 'Taken' })} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <Modal open={teamAbsencesOpen} onClose={() => setTeamAbsencesOpen(false)} C={C}>
        <TeamAbsences onClose={() => setTeamAbsencesOpen(false)} />
      </Modal>
      <Modal open={approvalsOpen} onClose={() => setApprovalsOpen(false)} C={C}>
        <ApprovalsPanel onClose={() => setApprovalsOpen(false)} onChanged={() => setRefreshKey((k) => k + 1)} />
      </Modal>
    </div>
  );
}

// ── small inline-style helpers ──────────────────────────────────────────────
function InfoRow({ C, icon: Icon, label, value }: { C: ColorSet; icon: any; label: string; value: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color={C.charcoalLight} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 10, color: C.charcoalLight, marginBottom: 2 }}>{label}</p>
        <p style={{ fontSize: 12, fontWeight: 600, color: C.charcoal }}>{value}</p>
      </div>
    </div>
  );
}

function StatusRow({ C, dot, label, value }: { C: ColorSet; dot: string; label: string; value: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot }} />
        <span style={{ fontSize: 12, fontWeight: 500, color: C.charcoal }}>{label}</span>
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>{value}</span>
    </div>
  );
}

function LegendDot({ C, color, label }: { C: ColorSet; color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: C.charcoalMid }}>{label}</span>
    </div>
  );
}

const primaryBtn = (C: ColorSet): React.CSSProperties => ({
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: '100%',
  padding: "12px 16px", borderRadius: 10, border: "none", background: C.orange, color: "#fff",
  fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
});
const outlineBtn = (C: ColorSet): React.CSSProperties => ({
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: '100%',
  padding: "12px 16px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent",
  color: C.charcoalMid, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
});

export default Page;
